import { isValidEmail } from './utils';
import { isKnownCountryCode } from './geo';

export const MAX_BILLING_NAME_LEN = 120;
export const MAX_BILLING_EMAIL_LEN = 254;
export const MAX_BILLING_ADDRESS_LEN = 200;
export const MAX_BILLING_COMPANY_LEN = 200;
export const MAX_BILLING_VAT_LEN = 32;
export const MAX_BILLING_STREET_LEN = 200;
export const MAX_BILLING_CITY_LEN = 120;
export const MAX_BILLING_STATE_LEN = 120;
export const MAX_BILLING_ZIP_LEN = 20;

export const BILLING_FIELD_KEYS = ['name', 'email', 'country', 'address', 'company', 'vat', 'street', 'city', 'state', 'zip'];

const KEYS = BILLING_FIELD_KEYS;

const RE_POSTAL = /^[A-Za-z0-9][A-Za-z0-9 -]*$/;
const RE_VAT = /^[A-Za-z0-9][A-Za-z0-9.\s-]*$/;
const RE_NAME = /^[A-Za-z][A-Za-z\s.'-]*$/;
const RE_PLACE = /^[A-Za-z][A-Za-z0-9\s.,'-]*$/;
const RE_STREET = /^[A-Za-z0-9][A-Za-z0-9\s.,#'/&()-]*$/;

const MAX_BY_KEY = {
    name: MAX_BILLING_NAME_LEN,
    email: MAX_BILLING_EMAIL_LEN,
    address: MAX_BILLING_ADDRESS_LEN,
    company: MAX_BILLING_COMPANY_LEN,
    vat: MAX_BILLING_VAT_LEN,
    street: MAX_BILLING_STREET_LEN,
    city: MAX_BILLING_CITY_LEN,
    state: MAX_BILLING_STATE_LEN,
    zip: MAX_BILLING_ZIP_LEN,
};

const msgIf = (cond, message) => (cond ? message : null);

const validatePostalCode = (zip) => (
    msgIf(!zip, 'Postal code is required.')
    || msgIf(zip.length < 2, 'Postal code must be at least 2 characters.')
    || msgIf(zip.length > MAX_BILLING_ZIP_LEN, `Postal code must be at most ${MAX_BILLING_ZIP_LEN} characters.`)
    || msgIf(!RE_POSTAL.test(zip), 'Postal code may only contain letters, numbers, spaces, and hyphens.')
    || msgIf(zip.length >= 3 && !/\d/.test(zip), 'Enter a valid postal code (include at least one number).')
);

const validateOptionalVat = (vat) => (
    msgIf(vat && vat.length < 2, 'VAT number must be at least 2 characters.')
    || msgIf(vat && vat.length > MAX_BILLING_VAT_LEN, `VAT number must be at most ${MAX_BILLING_VAT_LEN} characters.`)
    || msgIf(vat && !RE_VAT.test(vat), 'VAT number may only contain letters, numbers, spaces, dots, and hyphens.')
);

const validateName = (name) => (
    msgIf(!name, 'Name is required.')
    || msgIf(name.length < 2, 'Name must be at least 2 characters.')
    || msgIf(name.length > MAX_BILLING_NAME_LEN, `Name must be at most ${MAX_BILLING_NAME_LEN} characters.`)
    || msgIf(!RE_NAME.test(name), 'Name may only contain letters, spaces, and . \' -')
);

const validateCountry = (country) => (
    msgIf(!country, 'Country is required.')
    || msgIf(!isKnownCountryCode(country), 'Select a valid country.')
);

const validateStreet = (street) => (
    msgIf(!street, 'Street address is required.')
    || msgIf(street.length < 3, 'Street address must be at least 3 characters.')
    || msgIf(street.length > MAX_BILLING_STREET_LEN, `Street must be at most ${MAX_BILLING_STREET_LEN} characters.`)
    || msgIf(!RE_STREET.test(street), 'Street may only contain letters, numbers, and common address characters.')
);

const validateOptionalPlace = (value, label, max) => (
    msgIf(value && value.length < 2, `${label} must be at least 2 characters.`)
    || msgIf(value && value.length > max, `${label} must be at most ${max} characters.`)
    || msgIf(value && !RE_PLACE.test(value), `${label} may only contain letters, numbers, spaces, and . , ' -`)
);

const validateOptionalAddressLine = (address) => (
    msgIf(address && address.length < 2, 'Address line must be at least 2 characters.')
    || msgIf(address && address.length > MAX_BILLING_ADDRESS_LEN, `Address line must be at most ${MAX_BILLING_ADDRESS_LEN} characters.`)
    || msgIf(address && !RE_STREET.test(address), 'Address line may only contain letters, numbers, and common address characters.')
);

const validateEmail = (email) => (
    msgIf(!email, 'Billing email is required.')
    || msgIf(email.length > MAX_BILLING_EMAIL_LEN, 'Billing email is too long.')
    || msgIf(!isValidEmail(email), 'Enter a valid billing email address.')
);

/** Strip invalid characters while typing (frontend input guard). */
export const sanitizeBillingFieldValue = (key, value) => {
    if (key === 'country') {
        return String(value ?? '').trim().toUpperCase().slice(0, 2);
    }

    const max = MAX_BY_KEY[key] || 200;
    let v = String(value ?? '');

    switch (key) {
        case 'zip':
            v = v.replace(/[^A-Za-z0-9 -]/g, '');
            break;
        case 'vat':
            v = v.replace(/[^A-Za-z0-9.\s-]/g, '');
            break;
        case 'name':
            v = v.replace(/[^A-Za-z\s.'-]/g, '');
            break;
        case 'city':
        case 'state':
        case 'company':
            v = v.replace(/[^A-Za-z0-9\s.,'-]/g, '');
            break;
        case 'street':
        case 'address':
            v = v.replace(/[^A-Za-z0-9\s.,#'/&()-]/g, '');
            break;
        case 'email':
            v = v.replace(/\s/g, '');
            break;
        default:
            break;
    }

    return v.slice(0, max);
};

/** Single-field error for inline UI (same rules as save / backend). */
export const getBillingFieldError = (key, fields) => {
    const data = normalizeBillingDetailsFields(fields);

    switch (key) {
        case 'name':
            return validateName(data.name);
        case 'email':
            return validateEmail(data.email);
        case 'country':
            return validateCountry(data.country);
        case 'street':
            return validateStreet(data.street);
        case 'address':
            return validateOptionalAddressLine(data.address);
        case 'company':
            return validateOptionalPlace(data.company, 'Company name', MAX_BILLING_COMPANY_LEN);
        case 'vat':
            return validateOptionalVat(data.vat);
        case 'city':
            return validateOptionalPlace(data.city, 'Town/City', MAX_BILLING_CITY_LEN);
        case 'state':
            return validateOptionalPlace(data.state, 'State/County', MAX_BILLING_STATE_LEN);
        case 'zip':
            return validatePostalCode(data.zip);
        default:
            return null;
    }
};

/** Trim strings for API payload. */
export const normalizeBillingDetailsFields = (fields) => {
    const out = {};
    for (const key of KEYS) {
        out[key] = String(fields?.[key] ?? '').trim();
    }
    if (out.country) {
        out.country = out.country.toUpperCase();
    }
    return out;
};

/** All field errors for form (show after blur or on save). */
export const getBillingFieldErrors = (fields) => {
    const errors = {};
    for (const key of KEYS) {
        const err = getBillingFieldError(key, fields);
        if (err) errors[key] = err;
    }
    return errors;
};

/** Client validation before POST /auth/dqin/profile/update. */
export const validateBillingDetailsFields = (fields) => {
    const data = normalizeBillingDetailsFields(fields);
    const errors = getBillingFieldErrors(data);
    const firstKey = KEYS.find((k) => errors[k]);

    if (firstKey) {
        return { ok: false, message: errors[firstKey], errors };
    }

    return { ok: true, data, errors: {} };
};
